import { CameraControlsImpl } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import {
  Box3,
  BoxGeometry,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Vector3,
  type Object3D,
} from 'three'

import { useLevelGeometry } from '@hooks'
import { useGameStore } from '@stores'
import { useHelpersControls } from '@utils'

export function GameCameraRig() {
  const { grid, topView } = useHelpersControls()

  const { controls, scene, size: windowSize } = useThree()
  const { size } = useLevelGeometry()

  const hasZoomedIn = useRef(false)
  const levelComplete = useGameStore(state => state.levelComplete)
  const trainTarget = useRef<Object3D | null>(null)
  const currentTrainPos = useRef(new Vector3())

  // On level complete: zoom in on train and restrict panning to level bounds
  useEffect(() => {
    if (!levelComplete || !controls) return
    if (size.visibleWidth === 0 || size.height === 0) return

    const cc = controls as CameraControlsImpl

    const center = new Vector3(size.centerOffset, 0, 0)
    const boxSize = new Vector3(size.visibleWidth - 2, 1, size.height).multiplyScalar(2)
    const box = new Box3().setFromCenterAndSize(center, boxSize)
    cc.setBoundary(box)
    cc.zoomTo(windowSize.height * (450 / 1080), true)
    hasZoomedIn.current = false

    return () => {
      cc.setBoundary()
    }
  }, [windowSize, levelComplete, controls, size])

  // Overview fit when NOT following train
  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (!controls || levelComplete) return
    if (size.visibleWidth === 0 || size.height === 0) return

    const cc = controls as CameraControlsImpl

    const center = new Vector3(size.centerOffset, 0, 0)
    const boxSize = new Vector3(size.visibleWidth * 1.25, 0.1, size.height * 1.25)
    const box = new Box3().setFromCenterAndSize(center, boxSize)

    if (!hasZoomedIn.current) {
      hasZoomedIn.current = true
      const defaultSmoothTime = cc.smoothTime
      // eslint-disable-next-line react-hooks/immutability
      cc.smoothTime = 1.5
      setTimeout(() => (cc.smoothTime = defaultSmoothTime), 5000)
    }

    // ensure right camera positioning
    for (let i = 0; i < 3; i++) {
      cc.fitToBox(box, true, {
        paddingTop: 1.5,
        paddingLeft: 1,
        paddingRight: 1,
      })

      cc.rotatePolarTo(MathUtils.degToRad(topView ? 0 : 45))
      cc.rotateAzimuthTo(MathUtils.degToRad(topView ? 0 : -45))
    }

    let debugMesh: Mesh<BoxGeometry, MeshBasicMaterial> | null = null
    if (grid) {
      debugMesh = new Mesh(
        new BoxGeometry(...boxSize),
        new MeshBasicMaterial({ color: 'blue', wireframe: true }),
      )
      debugMesh.position.copy(center)
      scene.add(debugMesh)
    }

    return () => {
      if (!debugMesh) return
      debugMesh.geometry.dispose()
      debugMesh.material.dispose()
      scene.remove(debugMesh)
    }
  }, [windowSize, controls, topView, size, scene, grid, levelComplete])

  // Follow locomotive every frame
  useFrame(() => {
    if (!controls || !levelComplete) return
    const cc = controls as CameraControlsImpl

    if (!trainTarget.current) {
      trainTarget.current = scene.getObjectByName('L1') || null
    }

    if (trainTarget.current) {
      trainTarget.current.getWorldPosition(currentTrainPos.current)

      cc.moveTo(
        currentTrainPos.current.x,
        currentTrainPos.current.y + 0.5,
        currentTrainPos.current.z,
        true,
      )
    }
  })

  return null
}
